import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { toast } from 'sonner';
import { 
  Search, Check, X, Eye, Plus, Minus, Building2, 
  Star, Zap, Crown, Diamond, Clock, Wallet, Users
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const tierConfig = {
  none: { name: 'None', icon: Star, color: 'bg-gray-200 text-gray-600' },
  bronze: { name: 'Bronze', icon: Star, color: 'bg-amber-100 text-amber-700' },
  silver: { name: 'Silver', icon: Zap, color: 'bg-gray-200 text-gray-700' },
  gold: { name: 'Gold', icon: Crown, color: 'bg-yellow-100 text-yellow-700' },
  platinum: { name: 'Platinum', icon: Diamond, color: 'bg-purple-100 text-purple-700' },
};

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700' },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700' },
  suspended: { label: 'Suspended', color: 'bg-gray-200 text-gray-700' },
};

const AdminPartners = () => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [creditAdjustment, setCreditAdjustment] = useState({ amount: 0, reason: '' });
  const [processingAction, setProcessingAction] = useState(false);

  useEffect(() => {
    fetchPartners();
  }, [statusFilter]);

  const fetchPartners = async () => {
    const token = localStorage.getItem('token');
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const response = await axios.get(`${API}/admin/partners${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPartners(response.data);
    } catch (error) {
      toast.error('Failed to load partners');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (partnerId) => {
    const token = localStorage.getItem('token');
    setProcessingAction(true);
    try {
      await axios.put(`${API}/admin/partners/${partnerId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Partner approved successfully');
      fetchPartners();
    } catch (error) {
      toast.error('Failed to approve partner');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleReject = async (partnerId, reason) => {
    const token = localStorage.getItem('token');
    setProcessingAction(true);
    try {
      await axios.put(`${API}/admin/partners/${partnerId}/reject`, { reason }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Partner rejected');
      fetchPartners();
    } catch (error) {
      toast.error('Failed to reject partner');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleCreditAdjustment = async () => {
    if (!selectedPartner || !creditAdjustment.reason) {
      toast.error('Please provide a reason');
      return;
    }

    const token = localStorage.getItem('token');
    setProcessingAction(true);
    try {
      await axios.post(`${API}/admin/partners/${selectedPartner.id}/credits`, creditAdjustment, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Credits adjusted by ₹${creditAdjustment.amount.toLocaleString('en-IN')}`);
      setShowCreditModal(false);
      setCreditAdjustment({ amount: 0, reason: '' });
      fetchPartners();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust credits');
    } finally {
      setProcessingAction(false);
    }
  };

  const filteredPartners = partners.filter(partner => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      partner.company_name?.toLowerCase().includes(query) ||
      partner.contact_name?.toLowerCase().includes(query) ||
      partner.email?.toLowerCase().includes(query) ||
      partner.phone?.toLowerCase().includes(query)
    );
  });

  // Stats
  const pendingCount = partners.filter(p => p.status === 'pending').length;
  const approvedCount = partners.filter(p => p.status === 'approved').length;
  const totalCredits = partners.reduce((sum, p) => sum + (p.credit_balance || 0), 0);

  return (
    <AdminLayout>
      <div className="p-6" data-testid="admin-partners-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-playfair text-2xl font-bold text-[#1a1a1a]">Partners</h1>
            <p className="text-sm text-[#737373]">Manage partner applications and credits</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-[#e5e5e5] rounded-lg p-4">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{partners.length}</p>
                <p className="text-xs text-[#737373]">Total Partners</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#e5e5e5] rounded-lg p-4">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-amber-500" />
              <div>
                <p className="text-2xl font-bold">{pendingCount}</p>
                <p className="text-xs text-[#737373]">Pending Approval</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#e5e5e5] rounded-lg p-4">
            <div className="flex items-center gap-3">
              <Check className="w-8 h-8 text-green-500" />
              <div>
                <p className="text-2xl font-bold">{approvedCount}</p>
                <p className="text-xs text-[#737373]">Active Partners</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#e5e5e5] rounded-lg p-4">
            <div className="flex items-center gap-3">
              <Wallet className="w-8 h-8 text-purple-500" />
              <div>
                <p className="text-2xl font-bold">₹{(totalCredits / 100000).toFixed(1)}L</p>
                <p className="text-xs text-[#737373]">Total Credits</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-[#e5e5e5] rounded-lg p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#a3a3a3]" />
              <input
                type="text"
                placeholder="Search by company, name, email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-[#e5e5e5] rounded-lg focus:border-[#D4AF37] focus:outline-none"
                data-testid="search-input"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-[#e5e5e5] rounded-lg focus:border-[#D4AF37] focus:outline-none"
              data-testid="status-filter"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
        </div>

        {/* Partners List */}
        <div className="bg-white border border-[#e5e5e5] rounded-lg overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full mx-auto"></div>
            </div>
          ) : filteredPartners.length === 0 ? (
            <div className="p-12 text-center text-[#737373]">
              <Building2 className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No partners found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#f5f5f5] border-b border-[#e5e5e5]">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#737373] uppercase">Company</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#737373] uppercase">Contact</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#737373] uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#737373] uppercase">Tier</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-[#737373] uppercase">Credits</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-[#737373] uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e5e5e5]">
                  {filteredPartners.map((partner) => {
                    const status = statusConfig[partner.status] || statusConfig.pending;
                    const tier = tierConfig[partner.current_tier] || tierConfig.none;
                    const TierIcon = tier.icon;

                    return (
                      <tr key={partner.id} className="hover:bg-[#fafafa]" data-testid={`partner-row-${partner.id}`}>
                        <td className="px-4 py-4">
                          <p className="font-semibold text-[#1a1a1a]">{partner.company_name}</p>
                          <p className="text-sm text-[#737373]">{partner.email}</p>
                        </td>
                        <td className="px-4 py-4">
                          <p className="text-sm">{partner.contact_name}</p>
                          <p className="text-sm text-[#737373]">{partner.phone}</p>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
                            {status.label}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${tier.color}`}>
                            <TierIcon className="w-3 h-3" />
                            {tier.name}
                          </span>
                          {partner.discount_rate > 0 && (
                            <p className="text-xs text-[#D4AF37] mt-1">{partner.discount_rate}% off</p>
                          )}
                        </td>
                        <td className="px-4 py-4 text-right">
                          <p className="font-mono font-semibold">₹{(partner.credit_balance || 0).toLocaleString('en-IN')}</p>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-end gap-2">
                            {partner.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => handleApprove(partner.id)}
                                  disabled={processingAction}
                                  className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                  title="Approve"
                                  data-testid={`approve-btn-${partner.id}`}
                                >
                                  <Check className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={() => {
                                    const reason = prompt('Rejection reason:');
                                    if (reason) handleReject(partner.id, reason);
                                  }}
                                  disabled={processingAction}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Reject"
                                  data-testid={`reject-btn-${partner.id}`}
                                >
                                  <X className="w-5 h-5" />
                                </button>
                              </>
                            )}
                            {partner.status === 'approved' && (
                              <button
                                onClick={() => {
                                  setSelectedPartner(partner);
                                  setShowCreditModal(true);
                                }}
                                className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                                title="Adjust Credits"
                                data-testid={`adjust-credits-btn-${partner.id}`}
                              >
                                <Wallet className="w-5 h-5" />
                              </button>
                            )}
                            <button
                              onClick={() => {
                                setSelectedPartner(partner);
                                setShowDetailModal(true);
                              }}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="View Details"
                              data-testid={`view-btn-${partner.id}`}
                            >
                              <Eye className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedPartner && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b border-[#e5e5e5]">
              <div className="flex items-center justify-between">
                <h3 className="font-playfair text-xl font-bold">Partner Details</h3>
                <button onClick={() => setShowDetailModal(false)} className="text-[#737373] hover:text-[#1a1a1a]">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-[#737373] uppercase">Company</p>
                  <p className="font-semibold">{selectedPartner.company_name}</p>
                </div>
                <div>
                  <p className="text-xs text-[#737373] uppercase">Contact</p>
                  <p className="font-semibold">{selectedPartner.contact_name}</p>
                </div>
                <div>
                  <p className="text-xs text-[#737373] uppercase">Email</p>
                  <p>{selectedPartner.email}</p>
                </div>
                <div>
                  <p className="text-xs text-[#737373] uppercase">Phone</p>
                  <p>{selectedPartner.phone}</p>
                </div>
                <div>
                  <p className="text-xs text-[#737373] uppercase">GST Number</p>
                  <p>{selectedPartner.gst_number || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-[#737373] uppercase">Website</p>
                  <p>{selectedPartner.website || '-'}</p>
                </div>
              </div>
              <div>
                <p className="text-xs text-[#737373] uppercase">Address</p>
                <p>{selectedPartner.address || '-'}</p>
              </div>
              <div className="pt-4 border-t border-[#e5e5e5]">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-[#737373] uppercase">Credit Balance</p>
                    <p className="font-mono font-bold text-lg">₹{(selectedPartner.credit_balance || 0).toLocaleString('en-IN')}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#737373] uppercase">Total Topup</p>
                    <p className="font-mono">₹{(selectedPartner.total_topup_amount || 0).toLocaleString('en-IN')}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#737373] uppercase">Credits Used</p>
                    <p className="font-mono">₹{(selectedPartner.total_credits_used || 0).toLocaleString('en-IN')}</p>
                  </div>
                </div>
              </div>
              {selectedPartner.admin_notes && (
                <div className="pt-4 border-t border-[#e5e5e5]">
                  <p className="text-xs text-[#737373] uppercase">Admin Notes</p>
                  <p className="text-sm">{selectedPartner.admin_notes}</p>
                </div>
              )}
              <p className="text-xs text-[#a3a3a3]">
                Applied: {new Date(selectedPartner.created_at).toLocaleDateString('en-IN')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Credit Adjustment Modal */}
      {showCreditModal && selectedPartner && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-playfair text-xl font-bold">Adjust Credits</h3>
              <button onClick={() => setShowCreditModal(false)} className="text-[#737373] hover:text-[#1a1a1a]">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-[#737373]">Partner: <span className="font-semibold text-[#1a1a1a]">{selectedPartner.company_name}</span></p>
              <p className="text-sm text-[#737373]">Current Balance: <span className="font-mono font-semibold">₹{(selectedPartner.credit_balance || 0).toLocaleString('en-IN')}</span></p>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-manrope text-[#737373] mb-2">Amount</label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCreditAdjustment(prev => ({ ...prev, amount: Math.abs(prev.amount) }))}
                    className={`p-2 rounded-lg ${creditAdjustment.amount >= 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100'}`}
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setCreditAdjustment(prev => ({ ...prev, amount: -Math.abs(prev.amount) }))}
                    className={`p-2 rounded-lg ${creditAdjustment.amount < 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100'}`}
                  >
                    <Minus className="w-5 h-5" />
                  </button>
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#737373]">₹</span>
                    <input
                      type="number"
                      value={Math.abs(creditAdjustment.amount)}
                      onChange={(e) => {
                        const val = Number(e.target.value);
                        setCreditAdjustment(prev => ({
                          ...prev,
                          amount: prev.amount >= 0 ? val : -val
                        }));
                      }}
                      className="w-full pl-8 pr-4 py-2 border border-[#e5e5e5] rounded-lg focus:border-[#D4AF37] focus:outline-none font-mono"
                      data-testid="credit-amount-input"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-manrope text-[#737373] mb-2">Reason <span className="text-[#D4AF37]">*</span></label>
                <input
                  type="text"
                  value={creditAdjustment.reason}
                  onChange={(e) => setCreditAdjustment(prev => ({ ...prev, reason: e.target.value }))}
                  className="w-full px-4 py-2 border border-[#e5e5e5] rounded-lg focus:border-[#D4AF37] focus:outline-none"
                  placeholder="Reason for adjustment"
                  data-testid="credit-reason-input"
                />
              </div>

              {creditAdjustment.amount !== 0 && (
                <div className="bg-[#f5f5f5] rounded-lg p-4">
                  <p className="text-sm">
                    New Balance: <span className="font-mono font-bold">
                      ₹{((selectedPartner.credit_balance || 0) + creditAdjustment.amount).toLocaleString('en-IN')}
                    </span>
                  </p>
                </div>
              )}
            </div>

            <button
              onClick={handleCreditAdjustment}
              disabled={processingAction || !creditAdjustment.reason || creditAdjustment.amount === 0}
              className="w-full py-3 bg-[#D4AF37] text-white font-manrope uppercase tracking-widest hover:bg-[#B8960F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="confirm-adjustment-btn"
            >
              {processingAction ? 'Processing...' : 'Confirm Adjustment'}
            </button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminPartners;
